'use client';

import { useRef, useState } from 'react';
import { cn } from '../../lib/utils';

export function ContactForm({ fields, privacy, button, onSuccess, onError }) {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Zeitpunkt, an dem das Formular geladen wurde – für die Zeit-Falle (Bots
  // senden in Millisekunden ab, Menschen brauchen Sekunden).
  const renderedAt = useRef(Date.now());

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    fields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} ist erforderlich`;
      }

      if (field.type === 'email' && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.name])) {
          newErrors[field.name] = 'Bitte gib eine gültige E-Mail-Adresse ein';
        }
      }
    });

    if (privacy && !formData.privacy) {
      newErrors.privacy = 'Bitte akzeptiere die Datenschutzerklärung';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // API-Endpunkt aufrufen
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || '',
          email: formData.email || '',
          subject: formData.subject || '',
          message: formData.message || '',
          // Honeypot: bei echten Nutzern leer, da das Feld unsichtbar ist.
          website: formData.website || '',
          // Zeit-Falle: vergangene Sekunden seit Laden des Formulars.
          elapsed: Math.round((Date.now() - renderedAt.current) / 1000),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess?.();
        setFormData({});
      } else {
        // Server-Fehler oder Validierungsfehler
        if (data.errors) {
          setErrors(data.errors);
        } else {
          throw new Error(data.error || 'Ein Fehler ist aufgetreten');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/*
        Honeypot: für Menschen unsichtbar (CSS), für Bots aber im DOM sichtbar.
        Bewusst KEIN type="hidden" – das füllen viele Bots gerade nicht.
        aria-hidden + tabIndex={-1} hält es von Screenreadern und Tastatur fern.
      */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Bitte dieses Feld leer lassen</label>
        <input
          type="text"
          id="website"
          name="website"
          value={formData.website || ''}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {fields.map((field) => (
        <div key={field.name}>
          <label
            htmlFor={field.name}
            className="block text-sm font-semibold mb-2"
          >
            {field.label}
            {field.required && <span className="text-accent-dark dark:text-accent ml-1">*</span>}
          </label>

          {field.type === 'textarea' ? (
            <textarea
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              placeholder={field.placeholder}
              rows={field.rows || 4}
              className={cn(
                'w-full px-4 py-3 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-accent',
                errors[field.name] ? 'border-red-500' : 'border-gray-300'
              )}
            />
          ) : field.type === 'select' ? (
            <select
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className={cn(
                'w-full px-4 py-3 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-accent',
                errors[field.name] ? 'border-red-500' : 'border-gray-300'
              )}
            >
              <option value="">Bitte wählen...</option>
              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              placeholder={field.placeholder}
              className={cn(
                'w-full px-4 py-3 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-accent',
                errors[field.name] ? 'border-red-500' : 'border-gray-300'
              )}
            />
          )}

          {errors[field.name] && (
            <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
          )}
        </div>
      ))}

      {privacy && (
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="privacy"
              checked={formData.privacy || false}
              onChange={handleChange}
              className="mt-1"
            />
            <span
              className="text-sm text-gray-700"
              dangerouslySetInnerHTML={{
                __html: privacy.text.replace(
                  /\[(.*?)\]\((.*?)\)/g,
                  '<a href="$2" class="text-accent-dark dark:text-accent hover:underline">$1</a>'
                ),
              }}
            />
          </label>
          {errors.privacy && (
            <p className="text-red-500 text-sm mt-1">{errors.privacy}</p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className={cn(
          'w-full bg-accent hover:bg-accent/90 text-white font-bold px-8 py-4 rounded-lg transition-colors',
          isSubmitting && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isSubmitting ? button.loadingLabel : button.label}
      </button>
    </form>
  );
}
